import React, { Fragment, forwardRef } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { Link } from 'site-modules/shared/components/link/link';
import { personalizedSearchEntities } from 'site-modules/personalized-search/prop-types/prop.types';
import { VehicleEntities } from 'client/data/models/vehicle-v2';
import { getPlural } from 'client/utils/plural';
import { getValidImageUrl, SOURCES } from 'client/utils/image-helpers';
import { AuthorTooltip } from 'site-modules/shared/components/editorial/author-tooltip/author-tooltip';
import { getOpinionSourceUrl } from 'site-modules/personalized-search/utils/get-opinion-source-url';

import './opinion-summary.scss';

export const OpinionSummary = forwardRef(
  ({ opinionLabel: { labelDisplay, labelText, authors, entryId }, vehicle, isActiveTab }, ref) => (
    <div
      ref={ref}
      className={classnames(
        'opinion-summary w-100 d-flex rounded-16 bg-white flex-column text-cool-gray-10 size-16 p-1',
        {
          'pointer-events-none': !isActiveTab,
        }
      )}
      aria-hidden={!isActiveTab}
    >
      <h3 className="fw-bold mb-1 size-16 text-lowercase sentence-case">{labelDisplay}</h3>
      <p className="size-16 text-cool-gray-30 mb-1">
        <q>{labelText}</q>
        <Link
          to={getOpinionSourceUrl({ vehicle, labelText, expandedEntryId: entryId })}
          rel="noopener"
          className="source-link bg-blue-90 rounded-16 ms-0_5"
          aria-label={`Read more about ${labelDisplay}`}
          data-tracking-id="click_source_link"
        >
          <i className="text-blue-30 icon-link small" role="img" aria-hidden />
        </Link>
      </p>

      <div className="text-cool-gray-40 size-12 d-flex align-items-center gap-0_75">
        {!!authors?.length && (
          <>
            <div className="d-flex gap-0_75">
              {authors.map(
                author =>
                  author.image && (
                    <img
                      key={`${author.name}-photo`}
                      src={getValidImageUrl(author.image, SOURCES.STATIC)}
                      className="author-photo rounded object-fit-cover"
                      height={40}
                      width={40}
                      alt=""
                      loading="lazy"
                    />
                  )
              )}
            </div>
            <div>
              <div>
                by
                {authors.map((author, idx) => (
                  <Fragment key={author.name}>
                    {!!idx && ' and'}
                    <span className="author fw-bold text-cool-gray-40 align-baseline ps-0_25">{author.name}</span>
                    <AuthorTooltip aboutAuthor={author.bio} name={author.name} title={author.authorTitle} />
                  </Fragment>
                ))}
              </div>
              <div>Edmunds {getPlural('Expert', authors.length)}</div>
            </div>
          </>
        )}
        {!authors?.length && <div>by Edmunds Experts</div>}
      </div>
    </div>
  )
);

OpinionSummary.displayName = 'OpinionSummary';

OpinionSummary.propTypes = {
  vehicle: VehicleEntities.MakeModelSubmodelYear.isRequired,
  opinionLabel: personalizedSearchEntities.opinionLabelTypes.isRequired,
  isActiveTab: PropTypes.bool.isRequired,
};
